import { useCallback, useContext, useEffect } from 'react';
import { Codes } from '../SmartSendSocketsModels';
import { AccountCountCache, AccountNameCache, AccountSourceCache } from '../../../lib/cache/AccountNameCache';
import { ConcernsContext } from '../../../lib/context/ConcernsContext';
import { ConcernModel } from '../../concerns/Concern';
import { GetSocketDescription, GetSocketLink, GetSocketTitle } from '../../importstatus/GetSocketDescription';
import { addCache, getCached } from '../../../lib/helpers/ApiCache';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useVisible } from '~/apps/smartsend/hooks/useVisible';

const MARKETPLACE_TRIGGERED_CACHE = 'marketplace-trigger';

const useImportBackgroundTask = () => {
  const [windowInFocus] = useVisible();
  const { triggerAllImports } = useSmartSendApi();
  const { dispatch: cctxDispatch } = useContext(ConcernsContext);

  const updateImports = useCallback(async () => {
    if (!getCached('LOCALSTORAGE', MARKETPLACE_TRIGGERED_CACHE)) {
      addCache('LOCALSTORAGE', MARKETPLACE_TRIGGERED_CACHE, { triggered: true }, 1);

      const r = await triggerAllImports();
      if (r.success) {
        const accountConcerns: ConcernModel[] = [];
        const accounts: {
          Id: string;
          SourceId: string;
          SourceName: string;
          Description: string;
          Links: { [key: string]: string };
        }[] = [];

        // Store all the account
        r.result.Providers.forEach((i) => {
          AccountSourceCache[i.Id] = i.Name;
          AccountCountCache[i.Id] = i.Accounts.length;

          i.Accounts.forEach((a) => {
            if (a.Id) {
              const id = a.Id.replace(/[^\d.-]/g, '');
              if (!AccountNameCache[id]) {
                AccountNameCache[id] = a.Name;
              }
            }

            accounts.push({
              Id: a.Id,
              SourceId: i.Id,
              SourceName: i.Name,
              Description: a.Name,
              Links: a.Links
            });
          });
        });

        // Now do the concerns
        r.result.Providers.forEach((i) => {
          i.Accounts.filter((x) => x.Expired).forEach((a) => {
            if (!accountConcerns.find((x) => x.AccountId === a.Id)) {
              accountConcerns.push({
                Title: GetSocketTitle({ Code: Codes.CredentialsExpired, MarketplaceSource: i.Id }),
                Description: GetSocketDescription({
                  Code: Codes.CredentialsExpired,
                  MarketplaceSource: i.Id,
                  Stage: 'import'
                }),
                Link: GetSocketLink({
                  Code: Codes.CredentialsExpired,
                  MarketplaceSource: i.Id,
                  Links: a.Links
                }),
                AccountId: a.Id,
                Type: 'account',
                Integration: i.Id,
                AccountName: a.Name
              });
            }
          });

          i.Accounts.filter((x) => !x.Expired && x.SetupRequired).forEach((a) => {
            if (!accountConcerns.find((x) => x.AccountId === a.Id)) {
              accountConcerns.push({
                Title: GetSocketTitle({ Code: Codes.SetupRequired, MarketplaceSource: i.Id }),
                Description: GetSocketDescription({
                  Code: Codes.SetupRequired,
                  MarketplaceSource: i.Id,
                  Stage: 'import'
                }),
                Link: GetSocketLink({ Code: Codes.SetupRequired, MarketplaceSource: i.Id, Links: a.Links }),
                AccountId: a.Id,
                Type: 'account',
                Integration: i.Id,
                AccountName: a.Name
              });
            }
          });
        });

        cctxDispatch({ type: 'INITIALISE', payload: { accounts: accounts, concerns: accountConcerns } });
      }
    } else {
      console.log('BGTASK: Trigger already running');
    }
  }, [triggerAllImports, cctxDispatch]);

  useEffect(() => {
    // Maintain interval across window focus changes
    const run = async () => {
      if (!windowInFocus) return;
      console.log('BGTASK: Getting Claims Data as window in focus');

      try {
        await updateImports();
      } catch (error) {
        console.error('BGTASK: Error fetching import status:', error);
      }
    };

    run();
    const interval = setInterval(run, 10 * 60000);
    return () => clearInterval(interval);
  }, [updateImports, windowInFocus]);
};

export default useImportBackgroundTask;
